/**
 * DEPRECATED: Adapter to transform ContentReviewDTO for legacy polling
 * This adapter is used by pollContentReview which is legacy code
 * The new system uses getReviews/pollReviews with GenericReviewDTO
 * Keeping functional for backward compatibility during transition
 * 2024-01 - Dead code cleanup
 *
 * Layout:
 * - Center panel: Shows user's content (processed version)
 * - Right panel: Shows 5 criteria review cards + overall score
 * - Right panel detail: Shows individual criterion detail when clicked
 */

/**
 * Transform ContentReviewDTO to briefSections format
 * Creates ONE section with the user's content
 * The 5 criteria are stored as coach_details properties on this single section
 *
 * @param {Object} contentReview - ContentReviewDTO from backend
 * @returns {Array} briefSections - Array with ONE section containing the user's content
 */
export function adaptContentReviewToBriefSections(contentReview) {
  if (!contentReview || !contentReview.criteriaReviews) {
    return [];
  }

  const { content, criteriaReviews, status } = contentReview;

  // Create ONE section for the user's content
  // Store all 5 criteria as separate coach_detail entries keyed by criterionId
  const section = {
    heading: 'Your Content',
    headingID: 'user-content',
    text: content?.processed || content?.original || '',
    subitems: [],
    status: status === 'completed' ? 'answered' : 'unanswered',
    coachLoading: status !== 'completed',

    // Store multiple coach details - one per criterion
    // This will be used by SessionCoachDetailView when clicking a criterion card
    coach_details: {}
  };

  // Map each criterion's reviewDetail to coach_details
  criteriaReviews.forEach(criterion => {
    if (criterion.reviewDetail) {
      section.coach_details[criterion.criterionId] = {
        strengths: criterion.reviewDetail.strengths || '',
        gaps: criterion.reviewDetail.gaps || '',
        questions: criterion.reviewDetail.questions || '',
        examples: criterion.reviewDetail.examples || '',
        summary: criterion.reviewDetail.summary || '',
        status_label: criterion.reviewDetail.statusLabel || 'Pending',
        quality: criterion.score,
        status: mapScoreToStatus(criterion.score)  // Calculate from score
      };
    }
  });

  return [section];  // Return array with ONE section
}

/**
 * Map score (1-10) to status color (red/amber/green)
 * @param {number} score - Score from 1-10
 * @returns {string} Status color
 */
function mapScoreToStatus(score) {
  if (score === null || score === undefined) return 'amber';
  if (score >= 9) return 'green';  // Excellent
  if (score >= 7) return 'amber';  // Good
  return 'red';  // Needs Work
}

/**
 * Create individual criterion cards for the coach summary panel
 * @param {Object} contentReview - ContentReviewDTO from backend
 * @returns {Object} Coach summary with individual criteria cards
 */
export function adaptContentReviewToCoachSummary(contentReview) {
  if (!contentReview || !contentReview.criteriaReviews) {
    return { quality: null, criteria: [] };
  }

  const { criteriaReviews, overallQuality } = contentReview;

  // Create one card per criterion
  const criteria = criteriaReviews.map(criterion => ({
    criterionId: criterion.criterionId,
    criterionName: criterion.criterionName,
    score: criterion.score,
    priority: mapScoreToStatus(criterion.score),  // Calculate from score
    badge: criterion.reviewDetail?.statusLabel || 'Pending',
    title: criterion.criterionName,
    summary: criterion.reviewDetail?.summary || 'Review in progress...',
    status: criterion.status
  }));

  return {
    quality: overallQuality,
    criteria: criteria
  };
}
